/**
 * TasksReportsHub — Tasks Reports section under Reports.
 *
 * Owns ALL filter state in a single object and passes it to each sub-tab.
 * Phase 0 ships the Volume & Flow tab; remaining 4 tabs render placeholders
 * that surface the planned metrics so users can see the roadmap.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  BarChart3, ShieldCheck, Zap, AlertTriangle, Users,
} from 'lucide-react';
import DataTabs from '../data/DataTabs';
import TasksFilterBar, { TASK_FILTER_DEFAULTS } from './TasksFilterBar';
import { formatDateToLocal } from '../../lib/dateUtils';
import VolumeFlowTab from './tasks/VolumeFlowTab';
import QualityTab from './tasks/QualityTab';
import VelocityTab from './tasks/VelocityTab';
import DeadlinesTab from './tasks/DeadlinesTab';
import CapacityTab from './tasks/CapacityTab';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

const TasksReportsHub = () => {
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [filters, setFilters] = useState({
    ...TASK_FILTER_DEFAULTS,
    fromDate: formatDateToLocal(firstOfMonth),
    toDate: formatDateToLocal(today),
  });
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [subtypes, setSubtypes] = useState([]);

  // Load dropdown sources once on mount
  useEffect(() => {
    let cancelled = false;
    const loaders = [
      axios.get(`${API}/api/admin/employees`, { headers: auth() })
        .then((r) => !cancelled && setEmployees(Array.isArray(r.data) ? r.data : (r.data?.employees || [])))
        .catch(() => {}),
      axios.get(`${API}/api/admin/clients`, { headers: auth() })
        .then((r) => !cancelled && setClients(Array.isArray(r.data) ? r.data : (r.data?.clients || [])))
        .catch(() => {}),
      axios.get(`${API}/api/admin/departments`, { headers: auth() })
        .then((r) => {
          if (cancelled) return;
          const list = Array.isArray(r.data) ? r.data : (r.data?.departments || []);
          setDepartments(list.map((d) => d.name || d).filter(Boolean));
        })
        .catch(() => {}),
    ];
    Promise.allSettled(loaders);
    // Subtypes come from a known taxonomy — could be fetched but is stable.
    setSubtypes(['Reel', 'Post', 'Story', 'Calendar Shoot', 'Menu Shoot', 'FPV', 'Drone', 'Others']);
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="space-y-6" data-testid="tasks-reports-hub">
      <TasksFilterBar
        filters={filters}
        setFilters={setFilters}
        employees={employees}
        clients={clients}
        departments={departments}
        subtypes={subtypes}
      />
      <DataTabs
        defaultTab="volume"
        urlParam="tasks_subtab"
        testid="tasks-subtabs"
        tabs={[
          {
            key: 'volume', label: 'Volume & Flow', icon: BarChart3,
            render: () => <VolumeFlowTab filters={filters} />,
          },
          {
            key: 'quality', label: 'Quality', icon: ShieldCheck,
            render: () => <QualityTab filters={filters} />,
          },
          {
            key: 'velocity', label: 'Velocity', icon: Zap,
            render: () => <VelocityTab filters={filters} />,
          },
          {
            key: 'deadlines', label: 'Deadlines & SLA', icon: AlertTriangle,
            render: () => <DeadlinesTab filters={filters} />,
          },
          {
            key: 'capacity', label: 'Capacity & Workload', icon: Users,
            render: () => <CapacityTab filters={filters} />,
          },
        ]}
      />
    </div>
  );
};

export default TasksReportsHub;
